import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileUpload from '../common/FileUpload';
import MultiFileUpload from '../common/MultiFileUpload';
import { adminAPI } from '../../utils/api';

const PROMPT_PLACEHOLDERS = ['{question}', '{response}'];

const AssessmentCreate = ({ onAssessmentCreated }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [timePerQuestionMinutes, setTimePerQuestionMinutes] = useState(15);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLoadDefaultPrompt = async () => {
    setLoadingDefault(true);
    setError('');
    try {
      const data = await adminAPI.getDefaultSystemPrompt();
      setSystemPrompt(data.template || '');
    } catch (err) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoadingDefault(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!csvFile) {
      setError(t('admin.assessment.selectCsv'));
      return;
    }

    const trimmedPrompt = systemPrompt.trim();
    if (trimmedPrompt && PROMPT_PLACEHOLDERS.some((ph) => !trimmedPrompt.includes(ph))) {
      setError(t('admin.assessment.systemPromptPlaceholdersRequired'));
      return;
    }

    const parsedMinutes = Number(timePerQuestionMinutes);
    if (!Number.isInteger(parsedMinutes) || parsedMinutes < 1 || parsedMinutes > 240) {
      setError(t('admin.assessment.timePerQuestionInvalid'));
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('csv_file', csvFile);
      formData.append('time_per_question_minutes', String(parsedMinutes));
      if (trimmedPrompt) {
        formData.append('system_prompt', trimmedPrompt);
      }

      // Add attachment files if selected
      if (attachmentFiles && attachmentFiles.length > 0) {
        attachmentFiles.forEach((file) => {
          formData.append('attachment_files', file);
        });
      }

      const result = await adminAPI.createAssessment(formData);

      setSuccess(t('admin.assessment.createdSuccess', { count: result.question_count }));
      setName('');
      setDescription('');
      setSystemPrompt('');
      setTimePerQuestionMinutes(15);
      setCsvFile(null);
      setAttachmentFiles([]);

      if (onAssessmentCreated) {
        onAssessmentCreated(result);
      }
    } catch (err) {
      setError(err.message || t('admin.assessment.createdError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('admin.assessment.create')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('admin.assessment.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          margin="normal"
        />

        <TextField
          fullWidth
          label={t('admin.assessment.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          margin="normal"
        />

        <TextField
          fullWidth
          type="number"
          label={t('admin.assessment.timePerQuestion')}
          value={timePerQuestionMinutes}
          onChange={(e) => setTimePerQuestionMinutes(e.target.value)}
          margin="normal"
          inputProps={{ min: 1, max: 240, step: 1 }}
          helperText={t('admin.assessment.timePerQuestionHelp')}
          required
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('admin.assessment.csvUpload')}
          </Typography>
          <FileUpload
            id="csv-file-upload"
            onFileSelect={setCsvFile}
            selectedFile={csvFile}
            accept=".csv"
          />
        </Box>

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('admin.assessment.attachmentUpload')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('admin.assessment.attachmentNote')}
          </Typography>
          <MultiFileUpload
            id="attachment-files-upload"
            onFilesSelect={setAttachmentFiles}
            selectedFiles={attachmentFiles}
          />
        </Box>

        <Box sx={{ mt: 3, mb: 2 }}>
          <TextField
            fullWidth
            label={t('admin.assessment.systemPrompt')}
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            multiline
            rows={12}
            helperText={t('admin.assessment.systemPromptHelp')}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={handleLoadDefaultPrompt}
            disabled={loadingDefault || systemPrompt.trim().length > 0}
          >
            {loadingDefault
              ? t('admin.assessment.loadingDefaultPrompt')
              : t('admin.assessment.loadDefaultPrompt')}
          </Button>
        </Box>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? t('admin.assessment.creating') : t('admin.assessment.createButton')}
        </Button>
      </form>
    </Paper>
  );
};

export default AssessmentCreate;
